import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import Button from 'components/Button/Button';
import Panel from 'components/Panel/Panel';
import { BUTTON_VARIANT } from 'constants/interfaces';
import {
  useGetWatchCompanyRunsQuery,
  useGetWatchRunLlmStepsQuery,
} from 'store/slices/watchTower/apis/watchTowerApi';

import {
  continueBlock,
  formatInvestigationCost,
  investigationTime,
  parseInvestigationRuns,
  parseInvestigationSteps,
  runHasMergePatch,
} from './investigationRuns';
import InvestigationStepResponse from './InvestigationStepResponse';

import type { IWatchInvestigationLlmStep, IWatchInvestigationRun } from './investigationRuns';
import type { IInvestigationStepAttachment } from './investigationStepChat';

import styles from './scss/TargetedMarketing.module.scss';

interface IWatchTowerInvestigationRuns {
  companyId: number,
  onCopyStep: (attachment: IInvestigationStepAttachment) => void,
  onContinue: (runId: number) => Promise<void>,
}

function MetaItem({ label, value }: { label: string, value: string }): ReactElement {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function StepCard({
  step,
  open,
  onToggle,
  onCopy,
}: {
  step: IWatchInvestigationLlmStep,
  open: boolean,
  onToggle: () => void,
  onCopy: () => void,
}): ReactElement {
  const { t } = useTranslation();
  const hasResponse = step.responseJson != null;

  return (
    <article className={styles.runCard}>
      <button type="button" className={styles.nameButton} onClick={onToggle}>
        {`${step.stepIndex}. ${step.promptRole}`}
      </button>
      <dl className={styles.runMeta}>
        <MetaItem label={t('targetedMarketing.investigationRunStatus')} value={step.status} />
        <MetaItem
          label={t('targetedMarketing.investigationRunModel')}
          value={step.modelUsed || t('targetedMarketing.none')}
        />
        <MetaItem label={t('targetedMarketing.investigationRunTime')} value={investigationTime(step)} />
        <MetaItem
          label={t('targetedMarketing.investigationRunCost')}
          value={formatInvestigationCost(step.costUsd)}
        />
        <MetaItem
          label={t('targetedMarketing.investigationStepSearchCount')}
          value={step.searchCallCount == null ? t('targetedMarketing.none') : String(step.searchCallCount)}
        />
      </dl>
      {step.errorMessage ? <p className={styles.muted}>{step.errorMessage}</p> : null}
      <div className={styles.footerActions}>
        <Button
          title={t('targetedMarketing.investigationStepCopy')}
          variant={BUTTON_VARIANT.secondary}
          disabled={!hasResponse}
          onClick={onCopy}
        />
      </div>
      {!hasResponse ? (
        <p className={styles.muted}>{t('targetedMarketing.investigationStepNoResponse')}</p>
      ) : null}
      {open ? <InvestigationStepResponse responseJson={step.responseJson} /> : null}
    </article>
  );
}

function RunCard({
  companyId,
  run,
  runs,
  open,
  onToggle,
  onContinue,
  continuing,
  onCopyStep,
}: {
  companyId: number,
  run: IWatchInvestigationRun,
  runs: IWatchInvestigationRun[],
  open: boolean,
  onToggle: () => void,
  onContinue: (runId: number) => Promise<void>,
  continuing: boolean,
  onCopyStep: (attachment: IInvestigationStepAttachment) => void,
}): ReactElement {
  const { t } = useTranslation();
  const [openStepId, setOpenStepId] = useState<number | null>(null);
  const block = continueBlock(run.id, runs);
  const stepsQuery = useGetWatchRunLlmStepsQuery(open ? run.id : skipToken);
  const steps = parseInvestigationSteps(stepsQuery.data);

  return (
    <article className={styles.runCard}>
      <button type="button" className={styles.nameButton} onClick={onToggle}>
        {t('targetedMarketing.investigationRunLabel', { id: run.id })}
      </button>
      <dl className={styles.runMeta}>
        <MetaItem label={t('targetedMarketing.investigationRunStatus')} value={run.status} />
        <MetaItem
          label={t('targetedMarketing.investigationRunModel')}
          value={run.modelUsed || t('targetedMarketing.none')}
        />
        <MetaItem label={t('targetedMarketing.investigationRunTime')} value={investigationTime(run)} />
        <MetaItem
          label={t('targetedMarketing.investigationRunCost')}
          value={formatInvestigationCost(run.costUsd)}
        />
      </dl>
      {run.outputMessage ? (
        <p className={styles.muted}>
          {`${t('targetedMarketing.investigationRunOutput')}: ${run.outputMessage}`}
        </p>
      ) : null}
      <div className={styles.footerActions}>
        <Button
          title={t('targetedMarketing.investigationContinue')}
          disabled={block !== 'allowed' || continuing}
          isLoading={continuing}
          onClick={() => {
            if (block !== 'allowed' || continuing) {
              return;
            }
            onContinue(run.id).catch(() => undefined);
          }}
        />
      </div>
      {block === 'allowed' ? null : (
        <p className={styles.muted}>
          {t(`targetedMarketing.investigationContinueReason_${block}`)}
        </p>
      )}
      {open ? (
        <div className={styles.runList}>
          {stepsQuery.isError ? (
            <p className={styles.muted}>{t('targetedMarketing.investigationStepsError')}</p>
          ) : null}
          {stepsQuery.isLoading ? (
            <p className={styles.muted}>{t('targetedMarketing.investigationStepsLoading')}</p>
          ) : null}
          {!stepsQuery.isLoading && !stepsQuery.isError && steps.length === 0 ? (
            <p className={styles.muted}>{t('targetedMarketing.investigationStepsEmpty')}</p>
          ) : null}
          {!runHasMergePatch(steps) && steps.length > 0 ? (
            <p className={styles.muted}>{t('targetedMarketing.investigationStepConclusionsNote')}</p>
          ) : null}
          {steps.map((step) => (
            <StepCard
              key={step.id}
              step={step}
              open={openStepId === step.id}
              onToggle={() => setOpenStepId((current) => (current === step.id ? null : step.id))}
              onCopy={() => onCopyStep({
                watchCompanyId: companyId,
                runId: run.id,
                stepIndex: step.stepIndex,
                promptRole: step.promptRole,
                responseJson: step.responseJson,
              })}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
}

function WatchTowerInvestigationRuns({
  companyId,
  onCopyStep,
  onContinue,
}: IWatchTowerInvestigationRuns): ReactElement {
  const { t } = useTranslation();
  const [openRunId, setOpenRunId] = useState<number | null>(null);
  const [continuingId, setContinuingId] = useState<number | null>(null);
  const runsQuery = useGetWatchCompanyRunsQuery(companyId);
  const runs = parseInvestigationRuns(runsQuery.data);

  const onContinueRun = async (runId: number) => {
    setContinuingId(runId);
    try {
      await onContinue(runId);
    } finally {
      setContinuingId(null);
    }
  };

  return (
    <Panel className={styles.panel}>
      {runsQuery.isLoading ? (
        <p className={styles.muted}>{t('targetedMarketing.investigationRunsLoading')}</p>
      ) : null}
      {runsQuery.isError ? (
        <p className={styles.muted}>{t('targetedMarketing.investigationRunsError')}</p>
      ) : null}
      {!runsQuery.isLoading && !runsQuery.isError && runs.length === 0 ? (
        <p className={styles.muted}>{t('targetedMarketing.investigationRunsEmpty')}</p>
      ) : null}
      <div className={styles.runList}>
        {runs.map((run) => (
          <RunCard
            key={run.id}
            companyId={companyId}
            run={run}
            runs={runs}
            open={openRunId === run.id}
            continuing={continuingId === run.id}
            onToggle={() => setOpenRunId((current) => (current === run.id ? null : run.id))}
            onContinue={onContinueRun}
            onCopyStep={onCopyStep}
          />
        ))}
      </div>
    </Panel>
  );
}

export default WatchTowerInvestigationRuns;
